// DS: DS6.1.8 - List Item Radio Card
import React from 'react';
import c from 'classnames';
import omit from 'lodash/omit';
import RadioButton, {
  type RadioButtonPropsWithout,
} from '@wix/da-a11y/pkg/components/formControls/RadioButton';
import RadioButtonDS from '../../../../RadioButton/';
import { useListItemCard, CommonListItemCardProps } from '../../common';
import ListItemContent from '../../../ListItem/legos/ListItemContent';
import s from './ListItemCardRadioButton.scss';

export type Props = RadioButtonPropsWithout<'onChange' | 'prefix' | 'checked' | 'children'> &
  CommonListItemCardProps & {
    withControl?: boolean;
    labelClassName?: string;
    /**
     * Required because our headless RadioButton component only supports controlled inputs
     */
    onChange: (isSelected: boolean, value?: string) => void;
  };

const ListItemCardRadioButton: React.FC<Props> = ({
  withControl,
  labelClassName,
  ...unfilteredProps
}) => {
  const {
    rootProps: { name, value, onChange, checked, disabled, className, ...restRootProps },
    contentProps,
    ref,
  } = useListItemCard<HTMLInputElement, Props>(unfilteredProps);

  return withControl ? (
    <RadioButtonDS
      className={c(s['root'], className)}
      inputClassName={s['input']}
      name={name || ''}
      value={value || ''}
      onChange={onChange && (() => onChange(!checked, value))}
      checked={!!checked}
      disabled={disabled}
    >
      <ListItemContent {...contentProps} />
    </RadioButtonDS>
  ) : (
    <RadioButton
      ref={ref}
      name={name || ''}
      value={value || ''}
      className={labelClassName}
      onChange={onChange && (() => onChange(!checked, value))}
      checked={!!checked}
      disabled={disabled}
      // We need to be a bit wacky with aria prop passing and typing here to preserve the underlying RadioButton's
      // accessibility guardrails.
      hasAccessibleText={unfilteredProps.hasAccessibleText}
      aria-labelledby={unfilteredProps['aria-labelledby']}
      aria-label={unfilteredProps['aria-label']}
      {...omit(restRootProps, ['aria-label', 'aria-labelledby', 'hasAccessibleText'])}
    >
      <div className={c(s['root'], className)}>
        <ListItemContent {...contentProps} />
      </div>
    </RadioButton>
  );
};

ListItemCardRadioButton.displayName = 'ListItemCardRadioButton';

export default ListItemCardRadioButton;
